import type { HTMLAttributes } from "react";
import type { Column } from "@tanstack/react-table";
import { TableHead } from "../table";
import { Icon } from "../icon";
import { cn } from "~/lib/utils";

export const justifyToClass = {
  left: "",
  center: "mx-auto",
  right: "ml-auto",
};

export type DataTableColumnHeaderProps<TData, TValue> =
  HTMLAttributes<HTMLDivElement> & {
    column: Column<TData, TValue>;
    title?: string;
    justify?: "left" | "center" | "right";
  };

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
  className,
  justify = "left",
  children,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  if (!column.getCanSort()) {
    return (
      <TableHead className={cn(className, "cursor-default")}>{title}</TableHead>
    );
  }

  const sortOrder = column.getIsSorted();

  const handleSorting = () => {
    column.toggleSorting();
  };

  return (
    <TableHead
      onClick={handleSorting}
      className={cn(
        "group cursor-pointer",
        column.getIsSorted() &&
          "bg-muted/30 border-t-muted/80 border-x-[1.5px] border-x-muted/80",
        className
      )}
    >
      <span
        className={cn(
          "relative flex items-center gap-1 w-fit",
          justifyToClass[justify]
        )}
      >
        {children || <span className="truncate">{title}</span>}

        <span
          className={cn(
            "absolute -right-5 flex items-center text-card-foreground opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:animate-in group-hover:zoom-in-110",
            {
              "opacity-100 animate-in zoom-in-110": sortOrder,
              "rotate-180": sortOrder === "asc",
              "right-2": children,
            }
          )}
        >
          <Icon name="arrow-down" />
        </span>
      </span>
    </TableHead>
  );
};
